import { Link } from 'react-router-dom';
import { useAuthStore, useDashboardStore } from '@saiens/shared/stores';
import { useLeads, useOrders, useAcceptanceList } from '@saiens/shared/hooks';
import { cn } from '@saiens/shared/utils';
import { Pencil, X, RotateCcw, ChevronRight } from 'lucide-react';
import { FeatureGrid } from '@/components/dashboard/FeatureGrid';

function getDateDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

const today = new Date().toISOString().slice(0, 10);
const sevenDaysAgo = getDateDaysAgo(7);

function getEnglishDate(): string {
  const now = new Date();
  const weekday = now.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();
  const month = now.toLocaleDateString('en-US', { month: 'long' }).toUpperCase();
  const day = now.getDate();
  return `${weekday}, ${month} ${day}`;
}

export function DashboardPage() {
  const { employee } = useAuthStore();
  const { isEditMode, setEditMode, resetDefaults } = useDashboardStore();
  const { data: leadsData } = useLeads({ limit: 5, myLeads: true });
  const { data: ordersData } = useOrders({ limit: 5, myOrders: true });
  const { data: recentPayments } = useOrders({
    payDateFrom: sevenDaysAgo,
    payDateTo: today,
    myOrders: true,
    limit: 1,
  });
  const { data: unpaidAcceptances } = useAcceptanceList({
    unpaidOnly: true,
    limit: 1,
  });

  const name = employee?.name || '';
  const [firstName, ...rest] = name.split(/\s+/);

  return (
    <div className="px-5 py-8">
      {/* Hero greeting — Apple style */}
      <div className="animate-fade-in mb-8 flex items-start justify-between">
        <div>
          <p className="text-caption mb-1 text-accent">{getEnglishDate()}</p>
          <h1 className="text-display text-3xl text-foreground">
            {name ? (
              <>
                {firstName}
                {rest.length > 0 && <span className="text-muted-foreground"> {rest.join(' ')}</span>}
              </>
            ) : (
              '歡迎使用'
            )}
          </h1>
          <p className="text-display text-3xl text-muted-foreground">您好。</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={resetDefaults}
            className="glass-subtle flex h-9 w-9 items-center justify-center rounded-full border border-black/[0.06] text-muted-foreground transition-all duration-200 hover:text-foreground"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setEditMode(!isEditMode)}
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-full transition-all duration-200',
              isEditMode
                ? 'bg-primary text-white shadow-[0_2px_8px_rgba(0,113,227,0.3)]'
                : 'glass-subtle border border-black/[0.06] text-muted-foreground hover:text-foreground',
            )}
          >
            {isEditMode ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Feature Grid */}
      <div className="mb-8">
        <FeatureGrid />
      </div>

      {/* Quick Stats — Data visualization style */}
      {!isEditMode && (
        <div className="animate-fade-in-up stagger-8">
          <p className="text-caption mb-3 text-muted-foreground">概覽</p>
          <div className="grid grid-cols-2 gap-3">
            <StatCard
              label="進行中專案"
              value={leadsData?.total || 0}
            />
            <StatCard
              label="本月訂單"
              value={ordersData?.total || 0}
            />
            <StatCard
              label="近七日收款"
              value={recentPayments?.total || 0}
              to="/sale/recent-payments"
              accent
            />
            <StatCard
              label="未收款案件"
              value={unpaidAcceptances?.total || 0}
              to="/acceptance/payment-status"
              accent
            />
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  to,
  accent,
}: {
  label: string;
  value: number | string;
  to?: string;
  accent?: boolean;
}) {
  const content = (
    <div className="relative">
      <p className="text-display text-4xl text-foreground">{value}</p>
      <p className="mt-1.5 text-[11px] font-medium tracking-wide text-muted-foreground">{label}</p>
      {to && (
        <div className="absolute right-0 top-1/2 -translate-y-1/2">
          <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
        </div>
      )}
      {accent && (
        <div className="absolute -bottom-4 left-0 h-[2px] w-8 rounded-full bg-accent" />
      )}
    </div>
  );

  const cardClass = cn(
    'press-effect relative overflow-hidden rounded-2xl border border-black/[0.04] bg-white/80 p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-shadow',
    to && 'hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)]',
  );

  if (to) {
    return <Link to={to} className={cardClass}>{content}</Link>;
  }

  return <div className={cardClass}>{content}</div>;
}
